import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Wallet,
  CalendarDays,
  FileText,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import { apiRequest } from "../../utils/apiRequest";
import FullPageSpinner from "../../components/FullPageSpinner";

const formatCurrency = (amount) => {
  const value = Number(amount) || 0;

  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 2,
  }).format(value);
};

const formatHoursWorked = (totalMinutes) => {
  const safeMinutes = Math.max(0, Number(totalMinutes) || 0);
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;

  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentWeekRange = () => {
  const today = new Date();
  const currentDay = today.getDay();
  const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;

  const weekStartDate = new Date(today);
  weekStartDate.setHours(0, 0, 0, 0);
  weekStartDate.setDate(today.getDate() + mondayOffset);

  const weekEndDate = new Date(weekStartDate);
  weekEndDate.setDate(weekStartDate.getDate() + 6);

  return {
    weekStart: formatDateInputValue(weekStartDate),
    weekEnd: formatDateInputValue(weekEndDate),
  };
};

function PayslipModal({
  isOpen,
  onClose,
  onSave,
  saving,
  error,
  staff,
  weekStart,
  weekEnd,
}) {
  const [deductions, setDeductions] = useState([
    { deduction_label: "", amount: "" },
  ]);

  useEffect(() => {
    if (!staff) return;

    if (
      Array.isArray(staff.deductions_list) &&
      staff.deductions_list.length > 0
    ) {
      setDeductions(
        staff.deductions_list.map((item) => ({
          deduction_label: item.deduction_label || "",
          amount: String(item.amount ?? ""),
        })),
      );
    } else {
      setDeductions([{ deduction_label: "", amount: "" }]);
    }
  }, [staff]);

  const grossPay = Number(staff?.gross_pay) || 0;

  const normalizedDeductions = deductions
    .map((item) => ({
      deduction_label: item.deduction_label.trim(),
      amount: Math.max(0, Number(item.amount) || 0),
    }))
    .filter((item) => item.deduction_label || item.amount > 0);

  const totalDeductions = normalizedDeductions.reduce(
    (sum, item) => sum + item.amount,
    0,
  );

  const netPay = Math.max(0, grossPay - totalDeductions);

  const handleDeductionChange = (index, field, value) => {
    setDeductions((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const handleAddDeduction = () => {
    setDeductions((prev) => [...prev, { deduction_label: "", amount: "" }]);
  };

  const handleRemoveDeduction = (index) => {
    setDeductions((prev) => {
      if (prev.length === 1) {
        return [{ deduction_label: "", amount: "" }];
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!staff) return;

    onSave({
      staff_user_id: staff.staff_user_id,
      deductions: normalizedDeductions,
    });
  };

  if (!isOpen || !staff) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {staff.payslip_id ? "Edit Payslip" : "Generate Payslip"}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {staff.staff_name} • {staff.staff_id}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-slate-100"
          >
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-6">
            {error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                {error}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs text-slate-500">Period</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {formatDisplayDate(weekStart)} - {formatDisplayDate(weekEnd)}
                </p>
              </div>

              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs text-slate-500">Hours Worked</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {formatHoursWorked(staff.total_minutes)}
                </p>
              </div>

              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs text-slate-500">Hourly Rate</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {formatCurrency(staff.hourly_rate)}
                </p>
              </div>

              <div className="rounded-xl bg-emerald-50 p-4 ring-1 ring-emerald-100">
                <p className="text-xs text-emerald-600">Gross Pay</p>
                <p className="mt-1 text-sm font-semibold text-emerald-900">
                  {formatCurrency(grossPay)}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-700">
                  Deductions
                </label>

                <button
                  type="button"
                  onClick={handleAddDeduction}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Plus size={16} />
                  Add Deduction
                </button>
              </div>

              {deductions.map((item, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-[1fr_180px_auto]"
                >
                  <input
                    type="text"
                    value={item.deduction_label}
                    onChange={(e) =>
                      handleDeductionChange(
                        index,
                        "deduction_label",
                        e.target.value,
                      )
                    }
                    placeholder="Description"
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                  />

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.amount}
                    onChange={(e) =>
                      handleDeductionChange(index, "amount", e.target.value)
                    }
                    placeholder="Amount"
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                  />

                  <button
                    type="button"
                    onClick={() => handleRemoveDeduction(index)}
                    className="inline-flex items-center justify-center rounded-xl border border-red-200 bg-white px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-xl bg-red-50 p-4 ring-1 ring-red-100">
                <p className="text-xs text-red-600">Total Deductions</p>
                <p className="mt-1 text-lg font-bold text-red-900">
                  {formatCurrency(totalDeductions)}
                </p>
              </div>

              <div className="rounded-xl bg-amber-50 p-4 ring-1 ring-amber-100">
                <p className="text-xs text-amber-600">Net Pay</p>
                <p className="mt-1 text-lg font-bold text-amber-900">
                  {formatCurrency(netPay)}
                </p>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Payslip"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PayslipViewModal({ isOpen, onClose, payslip }) {
  if (!isOpen || !payslip) return null;

  const totalDeductions = Array.isArray(payslip.deductions_list)
    ? payslip.deductions_list.reduce(
        (sum, item) => sum + (Number(item.amount) || 0),
        0,
      )
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Payslip</h3>
            <p className="mt-1 text-sm text-slate-500">
              {payslip.staff_name} • {payslip.staff_id}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm text-slate-500 hover:bg-slate-100"
          >
            Close
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">Period</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {formatDisplayDate(payslip.period_start)} -{" "}
                {formatDisplayDate(payslip.period_end)}
              </p>
            </div>

            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">Pay Date</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {formatDisplayDate(payslip.pay_date)}
              </p>
            </div>

            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">Hours Worked</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {formatHoursWorked(payslip.total_minutes)}
              </p>
            </div>

            <div className="rounded-xl bg-slate-50 p-4">
              <p className="text-xs text-slate-500">Hourly Rate</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {formatCurrency(payslip.hourly_rate)}
              </p>
            </div>

            <div className="rounded-xl bg-emerald-50 p-4">
              <p className="text-xs text-emerald-600">Gross Pay</p>
              <p className="mt-1 text-sm font-semibold text-emerald-900">
                {formatCurrency(payslip.gross_pay)}
              </p>
            </div>

            <div className="rounded-xl bg-amber-50 p-4">
              <p className="text-xs text-amber-600">Net Pay</p>
              <p className="mt-1 text-sm font-semibold text-amber-900">
                {formatCurrency(payslip.net_pay)}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-4 py-3">
              <h4 className="text-sm font-semibold text-slate-900">
                Deductions
              </h4>
            </div>

            {!payslip.deductions_list ||
            payslip.deductions_list.length === 0 ? (
              <div className="px-4 py-4 text-sm text-slate-500">
                No deductions.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {payslip.deductions_list.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <p className="text-sm text-slate-700">
                      {item.deduction_label}
                    </p>
                    <p className="text-sm font-semibold text-red-600">
                      {formatCurrency(item.amount)}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-sm font-semibold text-slate-900">
                Total Deductions:{" "}
                <span className="text-red-600">
                  {formatCurrency(totalDeductions)}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WagesPage() {
  const token = localStorage.getItem("staff_portal_token");
  const defaultWeek = useMemo(() => getCurrentWeekRange(), []);

  const [weekStart, setWeekStart] = useState(defaultWeek.weekStart);
  const [weekEnd, setWeekEnd] = useState(defaultWeek.weekEnd);
  const [loading, setLoading] = useState(false);
  const [savingPayslip, setSavingPayslip] = useState(false);
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState("");
  const [wages, setWages] = useState([]);

  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  const periodLabel = useMemo(() => {
    if (!weekStart || !weekEnd) return "—";
    return `${formatDisplayDate(weekStart)} - ${formatDisplayDate(weekEnd)}`;
  }, [weekStart, weekEnd]);

  const handleLoadWages = useCallback(async () => {
    if (!weekStart || !weekEnd) {
      setError("Please select both week start and week end dates.");
      return;
    }

    if (new Date(weekEnd) < new Date(weekStart)) {
      setError("Week end date cannot be earlier than week start date.");
      return;
    }

    setLoading(true);
    setError("");

    const res = await apiRequest({
      script: "admin-weekly-wages",
      token,
      data: {
        week_start: weekStart,
        week_end: weekEnd,
      },
    });

    setLoading(false);

    if (!res?.success) {
      setError(res?.error || "Failed to load wages.");
      return;
    }

    setWages(res.wages || []);
  }, [token, weekStart, weekEnd]);

  useEffect(() => {
    handleLoadWages();
  }, [handleLoadWages]);

  const handleOpenPayslipModal = (staff) => {
    setModalError("");
    setSelectedStaff(staff);
    setShowPayslipModal(true);
  };

  const handleClosePayslipModal = () => {
    if (savingPayslip) return;
    setShowPayslipModal(false);
    setSelectedStaff(null);
    setModalError("");
  };

  const handleSavePayslip = async ({ staff_user_id, deductions }) => {
    setShowPayslipModal(false);
    setSavingPayslip(true);
    setModalError("");

    const res = await apiRequest({
      script: "admin-save-weekly-payslip",
      token,
      data: {
        staff_user_id,
        week_start: weekStart,
        week_end: weekEnd,
        deductions,
      },
    });

    setSavingPayslip(false);

    if (!res?.success) {
      setModalError(res?.error || "Failed to save payslip.");
      return;
    }

    if (res?.payslip_id) {
      handleDownloadPayslip(res.payslip_id);
    }

    handleLoadWages();
  };

  const handleDownloadPayslip = (payslipId) => {
    if (!payslipId || !token) return;

    const form = document.createElement("form");
    form.method = "POST";
    form.action = "https://staff.lasgidirestaurant.com/php/proxy.php";
    form.target = "_blank";
    form.style.display = "none";

    const fields = {
      script: "admin-download-payslip",
      api_token: token,
      payslip_id: String(payslipId),
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  return (
    <AdminLayout title="Wages">
      <FullPageSpinner show={loading || savingPayslip} />

      <div className="space-y-6">
        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        ) : null}

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Week Start
                </label>
                <input
                  type="date"
                  value={weekStart}
                  onChange={(e) => setWeekStart(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Week End
                </label>
                <input
                  type="date"
                  value={weekEnd}
                  onChange={(e) => setWeekEnd(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs text-slate-500">Selected Period</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {periodLabel}
                </p>
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={handleLoadWages}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800"
              >
                <CalendarDays size={18} />
                Load Wages
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-slate-50 to-emerald-50 px-6 py-5">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Weekly Wages
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Staff wages for the selected period
              </p>
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 ring-1 ring-emerald-200">
              <Wallet size={20} className="text-emerald-700" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr className="text-left">
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Staff
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Staff ID
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Hours Worked
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Hourly Rate
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Gross Pay
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {wages.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      No wage records available for the selected period.
                    </td>
                  </tr>
                ) : (
                  wages.map((item, index) => (
                    <tr
                      key={item.staff_user_id}
                      className={`border-b border-slate-100 last:border-b-0 ${
                        index % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                      }`}
                    >
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                        {item.staff_name}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700">
                        {item.staff_id}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700">
                        {formatHoursWorked(item.total_minutes)}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700">
                        {formatCurrency(item.hourly_rate)}
                      </td>

                      <td className="px-6 py-4 text-sm font-semibold text-emerald-700">
                        {formatCurrency(item.gross_pay)}
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleOpenPayslipModal(item)}
                            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white ${
                              item.payslip_id
                                ? "bg-amber-500 hover:bg-amber-600"
                                : "bg-emerald-600 hover:bg-emerald-700"
                            }`}
                          >
                            {item.payslip_id ? (
                              <Pencil size={16} />
                            ) : (
                              <FileText size={16} />
                            )}
                            {item.payslip_id
                              ? "Edit Payslip"
                              : "Generate Payslip"}
                          </button>

                          {item.payslip_id ? (
                            <button
                              type="button"
                              onClick={() =>
                                handleDownloadPayslip(item.payslip_id)
                              }
                              className="rounded-xl border border-blue-300 bg-white px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
                            >
                              Download PDF
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <PayslipModal
        isOpen={showPayslipModal}
        onClose={handleClosePayslipModal}
        onSave={handleSavePayslip}
        saving={savingPayslip}
        error={modalError}
        staff={selectedStaff}
        weekStart={weekStart}
        weekEnd={weekEnd}
      />

      <PayslipViewModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedPayslip(null);
        }}
        payslip={selectedPayslip}
      />
    </AdminLayout>
  );
}
